import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { HomeIcon, IdCardIcon, KeyRound, LogOut, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { mobileWorkspaces, unionWorkspaceEntries, type NavigationEntry } from '@/config/navigation';
import { VIEWS, type NavigableViewKey } from '@/config/views';
import { cn } from '@/shared/lib/cn';
import { useCanSee } from '@/shared/hooks/usePermission';

import { MoreMenu } from './MoreMenu';

const MAX_VISIBLE = 3;

interface Item {
  view: NavigableViewKey;
  icon: ReactNode;
  priority?: number;
}

import type { ReactNode } from 'react';

const HOME_ITEM: Item = { view: 'home', icon: <HomeIcon />, priority: 100 };

/* ------------------------------------------------------------------ */
/*  Individual tab item — checks its own permission                    */
/* ------------------------------------------------------------------ */

function MobileNavItem({ item, onVisible }: { item: Item; onVisible: (to: string) => void }) {
  const { t } = useTranslation();
  const canSee = useCanSee(item.view);
  const view = VIEWS[item.view];

  // Signal visibility after paint
  useEffect(() => {
    if (canSee && view.labelKey) onVisible(view.path);
  }, [canSee, view.labelKey, view.path, onVisible]);

  if (!canSee || !view.labelKey) return null;

  return <TabLink item={item} label={t(view.labelKey)} />;
}

/* ------------------------------------------------------------------ */
/*  Tab link (icon + label, active state)                               */
/* ------------------------------------------------------------------ */

function TabLink({ item, label }: { item: Item; label: string }) {
  const view = VIEWS[item.view];
  return (
    <NavLink
      to={view.path}
      end={view.path === '/'}
      className={({ isActive }) =>
        cn(
          'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] transition-colors',
          isActive ? 'text-brand-900 font-medium' : 'text-ink-muted active:text-ink',
        )
      }
    >
      {item.icon}
      <span className="max-w-18 truncate">{label}</span>
    </NavLink>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function MobileTabBar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [visibleSet, setVisibleSet] = useState<Set<string>>(new Set());
  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  // Track which items reported themselves visible
  const handleVisible = useCallback((to: string) => {
    setVisibleSet((prev) => {
      if (prev.has(to)) return prev;
      const next = new Set(prev);
      next.add(to);
      return next;
    });
  }, []);

  // Re-render once after mount so the primary/overflow split reflects visibility
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      // Batch: wait for all children to report, then re-render once
      const id = setTimeout(forceRender, 0);
      return () => clearTimeout(id);
    }
  }, []);

  const roles = user?.scope.is_global ? Object.keys(mobileWorkspaces) : (user?.role_codes ?? []);
  const entries = unionWorkspaceEntries(mobileWorkspaces, roles);
  const items: Item[] = [HOME_ITEM, ...entries.map((entry: NavigationEntry) => ({ ...entry }))];

  // Determine which items are visible, sorted by priority
  const visibleItems = items
    .filter((item) => visibleSet.has(VIEWS[item.view].path))
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

  const primary = visibleItems.slice(0, MAX_VISIBLE);
  const overflow = visibleItems.slice(MAX_VISIBLE);

  const closeMore = () => setMoreOpen(false);

  return (
    <>
      <MoreMenu open={moreOpen} onClose={closeMore}>
        <div className="border-border my-1 border-t" />

        {/* Navigation overflow items */}
        {overflow.map((item) => (
          <NavLink
            key={item.view}
            to={VIEWS[item.view].path}
            onClick={closeMore}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                isActive
                  ? 'bg-brand-50 text-brand-900 font-medium'
                  : 'text-ink hover:bg-surface-sunken',
              )
            }
          >
            {item.icon}
            {t(VIEWS[item.view].labelKey ?? '')}
          </NavLink>
        ))}

        <div className="border-border my-1 border-t" />

        {/* Account actions */}
        {user && (
          <>
            <button
              type="button"
              onClick={() => {
                closeMore();
                void navigate('/mi-ficha');
              }}
              className="text-ink hover:bg-surface-sunken flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors"
            >
              <IdCardIcon />
              {t('auth:user.myAccount')}
            </button>
            <button
              type="button"
              onClick={() => {
                closeMore();
                void navigate('/cambiar-contrasena');
              }}
              className="text-ink hover:bg-surface-sunken flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors"
            >
              <KeyRound />
              {t('auth:user.changePassword')}
            </button>
            <button
              type="button"
              onClick={() => {
                closeMore();
                void logout();
              }}
              className="text-ink hover:bg-surface-sunken flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors"
            >
              <LogOut />
              {t('auth:logout.action')}
            </button>
          </>
        )}
      </MoreMenu>

      {/* Hidden: all items for permission/visibility detection */}
      <div className="hidden" aria-hidden="true">
        {items.map((item) => (
          <MobileNavItem key={item.view} item={item} onVisible={handleVisible} />
        ))}
      </div>

      {/* Visible: primary tabs + More button */}
      <nav className="bg-surface border-border fixed bottom-0 inset-x-0 z-50 flex items-stretch border-t md:hidden">
        {primary.map((item) => (
          <TabLink key={item.view} item={item} label={t(VIEWS[item.view].labelKey ?? '')} />
        ))}
        {overflow.length > 0 && (
          <button
            type="button"
            onClick={() => setMoreOpen((open) => !open)}
            className={cn(
              'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] transition-colors',
              moreOpen ? 'text-brand-900 font-medium' : 'text-ink-muted active:text-ink',
            )}
          >
            <MoreHorizontal className="size-5" aria-hidden />
            <span>{t('nav.more')}</span>
          </button>
        )}
      </nav>
    </>
  );
}
